/**
 * Base Skeleton Components
 * Minimal, reusable building blocks for loading states
 *
 * Uses CSS shimmer animation for a professional look (like Facebook/LinkedIn)
 */

'use client';

import React, { useEffect } from 'react';

// Inject shimmer animation CSS once
let shimmerInjected = false;
function injectShimmerStyles() {
  if (shimmerInjected || typeof document === 'undefined') return;

  const style = document.createElement('style');
  style.id = 'skeleton-shimmer-styles';
  style.innerHTML = `
    @keyframes skeleton-shimmer {
      0% {
        background-position: -200% 0;
      }
      100% {
        background-position: 200% 0;
      }
    }

    .skeleton-shimmer {
      background: linear-gradient(
        90deg,
        var(--skeleton-base, rgba(0, 0, 0, 0.06)) 25%,
        var(--skeleton-highlight, rgba(0, 0, 0, 0.1)) 50%,
        var(--skeleton-base, rgba(0, 0, 0, 0.06)) 75%
      );
      background-size: 200% 100%;
      animation: skeleton-shimmer 1.5s ease-in-out infinite;
    }

    /* Dark mode support */
    .dark .skeleton-shimmer,
    [data-theme="dark"] .skeleton-shimmer {
      --skeleton-base: rgba(255, 255, 255, 0.06);
      --skeleton-highlight: rgba(255, 255, 255, 0.12);
    }
  `;
  document.head.appendChild(style);
  shimmerInjected = true;
}

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Base skeleton with shimmer animation
 * GPU-accelerated CSS animation for smooth performance
 */
export const Skeleton: React.FC<SkeletonProps> = ({ className = '', style }) => {
  useEffect(() => {
    injectShimmerStyles();
  }, []);

  return (
    <div
      className={`skeleton-shimmer rounded ${className}`}
      style={style}
    />
  );
};

/**
 * Circular skeleton for avatars
 */
export const SkeletonCircle: React.FC<SkeletonProps> = ({ className = '' }) => (
  <Skeleton className={`rounded-full ${className}`} />
);

/**
 * Text line skeleton
 */
export const SkeletonText: React.FC<SkeletonProps & { lines?: number }> = ({
  className = '',
  lines = 1
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        className="h-3"
        style={{ width: i === lines - 1 && lines > 1 ? '75%' : '100%' }}
      />
    ))}
  </div>
);

/**
 * Card skeleton - container with border and padding
 */
export const SkeletonCard: React.FC<SkeletonProps & { children?: React.ReactNode }> = ({
  className = '',
  children
}) => (
  <div className={`rounded-lg border border-border bg-card-bg p-4 ${className}`}>
    {children}
  </div>
);
